import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowRight, Search, Zap, Shield, Globe, Check } from "lucide-react";
import { SITE_URL } from "@/lib/site-url";
import { apiUrl } from "@/lib/api-base";
import { serviceSlug } from "@/lib/service-slugs";
import { TopBar } from "@/components/top-bar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";

const validLocales = ["en", "es", "fr", "de", "it", "pt", "zh", "ru"] as const;
type Locale = (typeof validLocales)[number];

// ─── i18n strings ──────────────────────────────────────────────────────────
const UI: Record<
  Locale,
  {
    title: string;
    subtitle: string;
    heading: string;
    search: string;
    service: string;
    price: string;
    from: string;
    available: string;
    getStarted: string;
    noResults: string;
    cta: string;
    ctaSub: string;
    ctaBtn: string;
    breadHome: string;
    breadPricing: string;
    metaTitle: string;
    metaDesc: string;
    popularTitle: string;
    whyTitle: string;
    why1: string;
    why2: string;
    why3: string;
  }
> = {
  en: {
    title: "SMS Verification Pricing — SmsEmpire",
    subtitle: "Transparent, pay-as-you-go prices for 200+ services worldwide.",
    heading: "Pricing",
    search: "Search services (Telegram, WhatsApp, Discord…)",
    service: "Service",
    price: "Price",
    from: "from",
    available: "numbers available",
    getStarted: "Get a number",
    noResults: "No services match your search.",
    cta: "Ready to verify?",
    ctaSub: "Create a free account and get your first number in seconds.",
    ctaBtn: "Start for free",
    breadHome: "Home",
    breadPricing: "Pricing",
    metaTitle: "SMS Verification Pricing — SmsEmpire | Pay-as-you-go from $0.35",
    metaDesc:
      "Transparent pricing for SMS verification numbers. Pay only for successful codes — from $0.35 per number. 200+ services: Telegram, WhatsApp, Discord, Instagram and more.",
    popularTitle: "Popular services",
    whyTitle: "Why SmsEmpire?",
    why1: "Cancel before a code arrives — within 20 minutes — and the full price returns to your balance.",
    why2: "200+ supported services across 40+ countries with live stock.",
    why3: "No monthly subscription — top up once and use whenever you need.",
  },
  es: {
    title: "Precios Verificación SMS — SmsEmpire",
    subtitle: "Precios transparentes de pago por uso para más de 200 servicios en todo el mundo.",
    heading: "Precios",
    search: "Buscar servicios (Telegram, WhatsApp, Discord…)",
    service: "Servicio",
    price: "Precio",
    from: "desde",
    available: "números disponibles",
    getStarted: "Obtener número",
    noResults: "Ningún servicio coincide con tu búsqueda.",
    cta: "¿Listo para verificar?",
    ctaSub: "Crea una cuenta gratis y obtén tu primer número en segundos.",
    ctaBtn: "Empezar gratis",
    breadHome: "Inicio",
    breadPricing: "Precios",
    metaTitle: "Precios Verificación SMS — SmsEmpire | Desde $0,35 por SMS",
    metaDesc:
      "Precios transparentes para números de verificación SMS. Paga solo por códigos exitosos — desde $0,35 por número. Más de 200 servicios: Telegram, WhatsApp, Discord, Instagram y más.",
    popularTitle: "Servicios populares",
    whyTitle: "¿Por qué SmsEmpire?",
    why1: "Cancela antes de que llegue el código —dentro de 20 minutos— y el importe íntegro vuelve a tu saldo.",
    why2: "Más de 200 servicios en más de 40 países con stock en tiempo real.",
    why3: "Sin suscripción mensual — recarga una vez y usa cuando quieras.",
  },
  fr: {
    title: "Tarifs Vérification SMS — SmsEmpire",
    subtitle: "Tarifs transparents au fur et à mesure pour plus de 200 services dans le monde.",
    heading: "Tarifs",
    search: "Rechercher des services (Telegram, WhatsApp, Discord…)",
    service: "Service",
    price: "Prix",
    from: "à partir de",
    available: "numéros disponibles",
    getStarted: "Obtenir un numéro",
    noResults: "Aucun service ne correspond à votre recherche.",
    cta: "Prêt à vérifier ?",
    ctaSub: "Créez un compte gratuit et obtenez votre premier numéro en quelques secondes.",
    ctaBtn: "Commencer gratuitement",
    breadHome: "Accueil",
    breadPricing: "Tarifs",
    metaTitle: "Tarifs Vérification SMS — SmsEmpire | À partir de 0,35 $",
    metaDesc:
      "Tarification transparente pour les numéros de vérification SMS. Payez uniquement pour les codes reçus — à partir de 0,35 $ par numéro. 200+ services: Telegram, WhatsApp, Discord, Instagram.",
    popularTitle: "Services populaires",
    whyTitle: "Pourquoi SmsEmpire ?",
    why1: "Annulez avant l'arrivée du code — sous 20 minutes — et le montant intégral revient sur votre solde.",
    why2: "200+ services pris en charge dans 40+ pays avec stock en temps réel.",
    why3: "Aucun abonnement mensuel — rechargez une fois et utilisez quand vous voulez.",
  },
  de: {
    title: "SMS-Verifizierungspreise — SmsEmpire",
    subtitle: "Transparente Pay-as-you-go-Preise für 200+ Dienste weltweit.",
    heading: "Preise",
    search: "Dienste suchen (Telegram, WhatsApp, Discord…)",
    service: "Dienst",
    price: "Preis",
    from: "ab",
    available: "verfügbare Nummern",
    getStarted: "Nummer holen",
    noResults: "Keine Dienste entsprechen Ihrer Suche.",
    cta: "Bereit zur Verifizierung?",
    ctaSub: "Erstellen Sie ein kostenloses Konto und erhalten Sie Ihre erste Nummer in Sekunden.",
    ctaBtn: "Kostenlos starten",
    breadHome: "Startseite",
    breadPricing: "Preise",
    metaTitle: "SMS-Verifizierungspreise — SmsEmpire | Ab $0,35",
    metaDesc:
      "Transparente Preise für SMS-Verifizierungsnummern. Zahlen Sie nur für erfolgreiche Codes — ab $0,35 pro Nummer. 200+ Dienste: Telegram, WhatsApp, Discord, Instagram.",
    popularTitle: "Beliebte Dienste",
    whyTitle: "Warum SmsEmpire?",
    why1: "Vor Eintreffen des Codes stornieren — innerhalb von 20 Minuten — und der volle Preis geht zurück auf Ihr Guthaben.",
    why2: "200+ unterstützte Dienste in 40+ Ländern mit Live-Bestand.",
    why3: "Kein monatliches Abonnement — einmalig aufladen und verwenden wann immer Sie wollen.",
  },
  it: {
    title: "Prezzi Verifica SMS — SmsEmpire",
    subtitle: "Prezzi trasparenti pay-as-you-go per oltre 200 servizi in tutto il mondo.",
    heading: "Prezzi",
    search: "Cerca servizi (Telegram, WhatsApp, Discord…)",
    service: "Servizio",
    price: "Prezzo",
    from: "da",
    available: "numeri disponibili",
    getStarted: "Ottieni un numero",
    noResults: "Nessun servizio corrisponde alla tua ricerca.",
    cta: "Pronto a verificare?",
    ctaSub: "Crea un account gratuito e ottieni il tuo primo numero in pochi secondi.",
    ctaBtn: "Inizia gratuitamente",
    breadHome: "Home",
    breadPricing: "Prezzi",
    metaTitle: "Prezzi Verifica SMS — SmsEmpire | Da $0,35",
    metaDesc:
      "Prezzi trasparenti per i numeri di verifica SMS. Paga solo per i codici ricevuti — da $0,35 per numero. 200+ servizi: Telegram, WhatsApp, Discord, Instagram.",
    popularTitle: "Servizi popolari",
    whyTitle: "Perché SmsEmpire?",
    why1: "Annulla prima che arrivi il codice — entro 20 minuti — e l'intero importo torna sul tuo saldo.",
    why2: "200+ servizi supportati in 40+ paesi con stock in tempo reale.",
    why3: "Nessun abbonamento mensile — ricarica una volta e usa quando vuoi.",
  },
  pt: {
    title: "Preços de Verificação SMS — SmsEmpire",
    subtitle:
      "Preços transparentes de pagamento por uso para mais de 200 serviços em todo o mundo.",
    heading: "Preços",
    search: "Pesquisar serviços (Telegram, WhatsApp, Discord…)",
    service: "Serviço",
    price: "Preço",
    from: "a partir de",
    available: "números disponíveis",
    getStarted: "Obter número",
    noResults: "Nenhum serviço corresponde à sua pesquisa.",
    cta: "Pronto para verificar?",
    ctaSub: "Crie uma conta gratuita e obtenha seu primeiro número em segundos.",
    ctaBtn: "Começar gratuitamente",
    breadHome: "Início",
    breadPricing: "Preços",
    metaTitle: "Preços Verificação SMS — SmsEmpire | A partir de $0,35",
    metaDesc:
      "Preços transparentes para números de verificação SMS. Pague apenas por códigos recebidos — a partir de $0,35 por número. 200+ serviços: Telegram, WhatsApp, Discord, Instagram.",
    popularTitle: "Serviços populares",
    whyTitle: "Por que SmsEmpire?",
    why1: "Cancela antes de o código chegar — em 20 minutos — e o valor integral volta ao teu saldo.",
    why2: "200+ serviços suportados em 40+ países com estoque em tempo real.",
    why3: "Sem assinatura mensal — recarregue uma vez e use quando quiser.",
  },
  ru: {
    title: "Цены на SMS-верификацию — SmsEmpire",
    subtitle: "Прозрачные цены с оплатой по факту для 200+ сервисов по всему миру.",
    heading: "Цены",
    search: "Поиск сервисов (Telegram, WhatsApp, Discord…)",
    service: "Сервис",
    price: "Цена",
    from: "от",
    available: "номеров доступно",
    getStarted: "Получить номер",
    noResults: "Сервисы не найдены.",
    cta: "Готовы верифицироваться?",
    ctaSub: "Создайте бесплатный аккаунт и получите первый номер за секунды.",
    ctaBtn: "Начать бесплатно",
    breadHome: "Главная",
    breadPricing: "Цены",
    metaTitle: "Цены на SMS-верификацию — SmsEmpire | От $0,35",
    metaDesc:
      "Прозрачные цены на номера для SMS-верификации. Платите только за полученные коды — от $0,35 за номер. 200+ сервисов: Telegram, WhatsApp, Discord, Instagram.",
    popularTitle: "Популярные сервисы",
    whyTitle: "Почему SmsEmpire?",
    why1: "Отмените до прихода кода — в течение 20 минут — и полная стоимость вернётся на баланс.",
    why2: "200+ сервисов в 40+ странах с актуальным наличием номеров.",
    why3: "Без ежемесячной подписки — пополните баланс один раз и пользуйтесь когда нужно.",
  },
  zh: {
    title: "SMS验证价格 — SmsEmpire",
    subtitle: "200多项全球服务的透明即用即付价格。",
    heading: "价格",
    search: "搜索服务（Telegram、WhatsApp、Discord…）",
    service: "服务",
    price: "价格",
    from: "从",
    available: "个号码可用",
    getStarted: "获取号码",
    noResults: "没有服务匹配您的搜索。",
    cta: "准备验证？",
    ctaSub: "创建免费账户，几秒内获得您的第一个号码。",
    ctaBtn: "免费开始",
    breadHome: "首页",
    breadPricing: "价格",
    metaTitle: "SMS验证价格 — SmsEmpire | 低至$0.35",
    metaDesc:
      "SMS验证号码的透明定价。仅为成功的验证码付费——每个号码低至$0.35。200多个服务：Telegram、WhatsApp、Discord、Instagram等。",
    popularTitle: "热门服务",
    whyTitle: "为什么选择SmsEmpire？",
    why1: "在验证码送达前取消——20 分钟内——全额退回余额。",
    why2: "40多个国家的200多项服务，实时库存。",
    why3: "无月费订阅——充值一次，随时使用。",
  },
};

// ─── Popular service codes for quick display (top 12) ──────────────────────
const POPULAR_CODES = ["wa", "tg", "ds", "ig", "fb", "go", "am", "tw", "nf", "ub", "ts", "uk"];

type Service = { code: string; name: string; price_available: number; count: number };

export const Route = createFileRoute("/$lang/pricing")({
  beforeLoad: ({ params }) => {
    if (!validLocales.includes(params.lang as Locale)) throw notFound();
  },
  loader: async ({ params }) => {
    const lang = params.lang as Locale;
    try {
      const res = await fetch(apiUrl("/api/public/services"));
      const services: Service[] = res.ok ? await res.json() : [];
      return { lang, services };
    } catch {
      return { lang, services: [] as Service[] };
    }
  },
  head: ({ params }) => {
    const lang = (params.lang as Locale) ?? "en";
    const ui = UI[lang] ?? UI.en;
    const canonical = `${SITE_URL}/${lang}/pricing`;
    return {
      meta: [
        { title: ui.metaTitle },
        { name: "description", content: ui.metaDesc },
        {
          name: "keywords",
          content:
            "sms verification price, virtual number price, sms number cost, cheap sms verification, sms empire pricing",
        },
        { property: "og:title", content: ui.metaTitle },
        { property: "og:description", content: ui.metaDesc },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
        { property: "og:image", content: `${SITE_URL}/og/pricing.png` },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: ui.metaTitle },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: `${SITE_URL}/og/pricing.png` },
      ],
      links: [
        { rel: "canonical", href: canonical },
        { rel: "alternate", hrefLang: "x-default", href: `${SITE_URL}/en/pricing` },
        ...validLocales.map((l) => ({
          rel: "alternate",
          hrefLang: l,
          href: `${SITE_URL}/${l}/pricing`,
        })),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: ui.metaTitle,
            description: ui.metaDesc,
            url: canonical,
            inLanguage: lang,
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: ui.breadHome, item: `${SITE_URL}/` },
                { "@type": "ListItem", position: 2, name: ui.breadPricing, item: canonical },
              ],
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "SMS Verification Services",
            numberOfItems: 200,
            itemListElement: POPULAR_CODES.slice(0, 10).map((code, i) => ({
              "@type": "ListItem",
              position: i + 1,
              item: {
                "@type": "Product",
                name: `SMS Verification — ${code.toUpperCase()}`,
                url: `${SITE_URL}/${lang}/sms/${code}`,
                offers: {
                  "@type": "Offer",
                  priceCurrency: "USD",
                  price: "0.35",
                  availability: "https://schema.org/InStock",
                },
              },
            })),
          }),
        },
      ],
    };
  },
  component: PricingPage,
} as any);

function PricingPage() {
  const { lang, services } = Route.useLoaderData() as { lang: Locale; services: Service[] };
  const ui = UI[lang] ?? UI.en;
  const { user } = useAuth();
  const [query, setQuery] = useState("");

  const popular = services.filter((s) => POPULAR_CODES.includes(s.code));
  const filtered =
    query.length >= 2
      ? services.filter((s) => s.name.toLowerCase().includes(query.toLowerCase()))
      : services;

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[400px] bg-gradient-hero"
        aria-hidden
      />
      <TopBar />
      <main className="relative mx-auto max-w-6xl px-6 pt-12 pb-24">
        {/* Breadcrumb */}
        <nav className="mb-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary">
            {ui.breadHome}
          </Link>
          <span>/</span>
          <span className="text-foreground font-medium">{ui.breadPricing}</span>
        </nav>

        {/* Hero */}
        <div className="mx-auto max-w-3xl text-center mb-16">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary mb-4">
            <Zap className="h-3.5 w-3.5" /> Pay-as-you-go
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
            {ui.heading}
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">{ui.subtitle}</p>
        </div>

        {/* Why SmsEmpire */}
        <section aria-labelledby="why-heading" className="mb-16">
          <h2 id="why-heading" className="sr-only">
            {ui.whyTitle}
          </h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              { icon: Check, text: ui.why1 },
              { icon: Globe, text: ui.why2 },
              { icon: Shield, text: ui.why3 },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-3 rounded-2xl border border-border bg-card p-5"
              >
                <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <item.icon className="h-4 w-4" />
                </div>
                <p className="text-sm text-muted-foreground">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Popular services */}
        {popular.length > 0 && (
          <section aria-labelledby="popular-heading" className="mb-16">
            <h2 id="popular-heading" className="mb-6 text-2xl font-bold tracking-tight">
              {ui.popularTitle}
            </h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {popular.slice(0, 6).map((s) => (
                <Link
                  key={s.code}
                  to="/$lang/sms/$service"
                  params={{ lang, service: serviceSlug(s.code) }}
                  className="group flex items-center justify-between rounded-2xl border border-border bg-card p-5 shadow-elegant transition-all hover:border-primary/40 hover:shadow-glow"
                >
                  <div>
                    <h3 className="font-semibold text-foreground group-hover:text-primary">
                      {s.name}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {s.count.toLocaleString()} {ui.available}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-primary">
                      ${s.price_available.toFixed(2)}
                    </span>
                    <ArrowRight className="ml-auto mt-1 h-4 w-4 text-muted-foreground group-hover:text-primary" />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Full price table */}
        <section aria-labelledby="all-prices-heading">
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <h2 id="all-prices-heading" className="text-2xl font-bold tracking-tight">
              {ui.service} &amp; {ui.price}
            </h2>
            <div className="relative max-w-sm w-full">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                id="pricing-search"
                type="search"
                placeholder={ui.search}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full rounded-full border border-border bg-card py-2 pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary/30"
              />
            </div>
          </div>

          {filtered.length === 0 ? (
            <p className="text-center py-16 text-muted-foreground">{ui.noResults}</p>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-elegant">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="py-3 pl-5 pr-3 text-left font-semibold text-foreground">
                      {ui.service}
                    </th>
                    <th className="py-3 px-3 text-right font-semibold text-foreground">
                      {ui.price}
                    </th>
                    <th className="py-3 pl-3 pr-5 text-right font-semibold text-foreground hidden sm:table-cell">
                      {ui.available}
                    </th>
                    <th className="py-3 pl-3 pr-5 text-right font-semibold text-foreground"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 200).map((s, idx) => (
                    <tr
                      key={s.code}
                      className={`border-b border-border/50 transition hover:bg-muted/20 ${idx % 2 === 0 ? "" : "bg-muted/10"}`}
                    >
                      <td className="py-3 pl-5 pr-3 font-medium text-foreground">
                        <Link
                          to="/$lang/sms/$service"
                          params={{ lang, service: serviceSlug(s.code) }}
                          className="hover:text-primary hover:underline"
                        >
                          {s.name}
                        </Link>
                      </td>
                      <td className="py-3 px-3 text-right font-bold text-primary">
                        {ui.from} ${s.price_available.toFixed(2)}
                      </td>
                      <td className="py-3 pl-3 pr-5 text-right text-muted-foreground hidden sm:table-cell">
                        {s.count > 0 ? s.count.toLocaleString() : "—"}
                      </td>
                      <td className="py-3 pl-3 pr-5 text-right">
                        <Link
                          to={user ? "/dashboard" : "/auth"}
                          search={user ? undefined : { tab: "register" }}
                          className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary hover:bg-primary/20"
                        >
                          {ui.getStarted} <ArrowRight className="h-3 w-3" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* CTA */}
        <section aria-labelledby="cta-heading" className="mt-16">
          <div className="mx-auto max-w-3xl rounded-3xl border border-primary/30 bg-gradient-primary p-10 text-center shadow-glow">
            <h2
              id="cta-heading"
              className="text-3xl font-bold tracking-tight text-primary-foreground sm:text-4xl"
            >
              {ui.cta}
            </h2>
            <p className="mt-3 text-primary-foreground/90">{ui.ctaSub}</p>
            <div className="mt-6">
              <Button asChild size="lg" variant="secondary">
                <Link
                  to={user ? "/dashboard" : "/auth"}
                  search={user ? undefined : { tab: "register" }}
                >
                  {ui.ctaBtn} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
